import { useDispatch } from "react-redux"
import { MyDispatch, MySelector } from "../redux/store"
import { checkVideo } from "../utils/isVideo"
import { useNavigate } from "react-router-dom"
import { useEffect, useState, useCallback } from "react"
import { getOptimalUrl } from "../utils/getOptimalUrl"
import {
  setCurrentFolderData,
  setCurrentFolderId,
  setAiSearchActive,
  setFullScreen,
  setFullScreenId,
  setFullScreenObject,
} from "../redux/utilSlice"
import { toast } from "sonner"

export const HandleGalleryFns = () => {
  const navigate = useNavigate()

  const dispatch = useDispatch<MyDispatch>()

  const { eventId, category } = MySelector((state) => state.event)

  const { allFiles, eventFolders } = MySelector((state) => state.gallery)

  const { fullScreenId } = MySelector((state) => state.util)

  const [folders, setFolders] = useState<any>()

  // to filter private folders when category = guest
  useEffect(() => {
    if (!eventFolders) return
    if (!category) return
    if (category === "host") setFolders(eventFolders)
    if (category === "guest") {
      const filteredFolders = eventFolders?.filter(
        (item: { openToPublic: false }) => item.openToPublic !== false
      )
      setFolders(filteredFolders)
    }
  }, [category, eventFolders])

  // fn to filter and store only photos from allFiles in currentFolderData
  const setAllPhotos = useCallback(() => {
    let photos = allFiles?.filter(
      (af: {
        id: string
        name: string
        url: string
        folderId: string
        compressedUrl?: string
        b2Url?: string
        b2CompressedUrl?: string
        openToPublic?: boolean
      }) => !checkVideo(getOptimalUrl(af, false))
    )
    
    // Filter out files that are not open to public for guests
    if (category === "guest") {
      photos = photos?.filter(
        (af: { openToPublic?: boolean }) => af.openToPublic !== false
      )
    }

    dispatch(setCurrentFolderData(photos))
    dispatch(setCurrentFolderId(""))
    dispatch(setAiSearchActive(false))
  }, [allFiles, category, dispatch])

  // fn to filter and store only videos from allFiles in currentFolderData
  const setAllVideos = useCallback(() => {
    let videos = allFiles?.filter(
      (af: {
        id: string
        name: string
        url: string
        folderId: string
        compressedUrl?: string
        b2Url?: string
        b2CompressedUrl?: string
        openToPublic?: boolean
      }) => checkVideo(getOptimalUrl(af, false))
    )
    
    // Filter out files that are not open to public for guests
    if (category === "guest") {
      videos = videos?.filter(
        (af: { openToPublic?: boolean }) => af.openToPublic !== false
      )
    }

    dispatch(setCurrentFolderData(videos))
    dispatch(setCurrentFolderId(""))
    dispatch(setAiSearchActive(false))
  }, [allFiles, category, dispatch])

  // fn to filter and store only photos from a particular folder in currentFolderData
  const setFolderFiles = useCallback((id: string) => {
    let assets = allFiles?.filter(
      (af: {
        id: string
        name: string
        url: string
        folderId: string
        compressedUrl?: string
        openToPublic?: boolean
      }) => af.folderId === id
    )
    
    // Filter out files that are not open to public for guests
    if (category === "guest") {
      assets = assets?.filter(
        (af: { openToPublic?: boolean }) => af.openToPublic !== false
      )
    }

    dispatch(setCurrentFolderData(assets))
    dispatch(setCurrentFolderId(id))
    dispatch(setAiSearchActive(false))
  }, [allFiles, category, dispatch])

  // set full screen on share when the site first loads
  const triggerFullScreen = () => {
    const fileObj = allFiles?.find(
      (af: { id: string }) => af.id === fullScreenId
    )
    
    // If guest trying to access private file, block and show error
    if (category === "guest" && fileObj?.openToPublic === false) {
      toast.error("This file is not available to guests")
      dispatch(setFullScreenId(null))
      return
    }
    
    dispatch(setFullScreenObject(fileObj))
    dispatch(setFullScreen(true))
    navigate(`/${eventId}/file/${fullScreenId}`)
    dispatch(setFullScreenId(null))
  }

  return {
    setAllPhotos,
    setAllVideos,
    setFolderFiles,
    triggerFullScreen,
    folders,
  }
}
